import React from 'react'
import classNames from 'classnames'
import Fab from '@mui/material/Fab'
import Tooltip from '@mui/material/Tooltip'
import FileInput from 'react-fine-uploader/file-input'
import { makeStyles } from '@mui/styles'

import ThemeInterface from '@/theme-interface'
import SvgIcon from '@/modules/base/components/svg-icon/svg-icon'
import { FineUploader } from 'fine-uploader'
// FLAG:useDocuAi — T25: the file types the DMS receipt upload accepts.
import { RECEIPT_DMS_TRIGGER_ALLOWED_EXTENSIONS } from '@/modules/receipt/helpers/dms-receipt-upload'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {},
  fab: {
    margin: 8,
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    transition: `${theme.transitions.create('transform', {
      duration: theme.transitions.duration.shorter,
    })}, opacity 0.8s`,
    opacity: 1,
  },
  fabClosed: {
    opacity: 0,
    transform: 'scale(0)',
  },
  fileInput: {
    display: 'flex !important',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    borderRadius: '100%',
    cursor: 'pointer',
    alignItems: 'center',
    '& > *': {
      cursor: 'pointer',
    },
  },
  hiddenInput: {
    display: 'none',
  },
  icon: {
    fill: theme.realBlack,

    '&:hover': {
      fill: theme.realBlack,
    },
  },
}))

type ReceiptUploadActionPropTypes = {
  accept: string,
  icon: string,
  label: string,
  isOpen?: boolean,
  uploader: FineUploader,
  /**
   * FLAG:useDocuAi — T25: send this upload through DMS→DocuAI like every other receipt upload.
   *
   * Until then this button was the last FineUploader entry point on the receipt form, and it
   * behaved differently from the dropzone right next to it: its own 25 MB client limit (plus the
   * 25 MB multer limit of /file/upload) refused a document the dropzone had accepted at 38 MB, it
   * checked image edge lengths nobody else checks, and its file carried no upload intent — so the
   * server could neither create a record nor link it, and an unsaved form left the document
   * orphaned in DMS (reported 2026-09-18, file 24190 „aral.pdf").
   */
  useDmsUpload?: boolean,
  onDmsUpload?: (files: File[]) => void,
}

const ReceiptUploadAction = (props: ReceiptUploadActionPropTypes) => {
  const classes = useStyles(props)
  const { accept, icon, label, isOpen, uploader, useDmsUpload, onDmsUpload } = props

  const buttonIcon = (
    <SvgIcon
      svg={icon}
      customClass={classes.icon}
      height="16px"
      width="16px" />
  )

  // FLAG:useDocuAi — a plain hidden input inside the round button, exactly the shape the
  // FineUploader's FileInput had. The shared DocumentsUploadTrigger was the obvious reuse, but it
  // brings its own markup (an UploadDropZone plus a visible trigger button), which broke out of
  // the 40px circle — reported 2026-09-18 („das bild ist aus dem kreis"). The upload itself is
  // identical either way: the files go to `onDmsUpload`, which runs the same DMS upload the
  // dropzone runs, with the same intent.
  const onPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Reset first: picking the SAME file twice in a row must fire again.
    event.target.value = ''
    if (files.length) { onDmsUpload?.(files) }
  }

  const picker = useDmsUpload
    ? (
      <label className={classes.fileInput} data-qs-id='receiptUploadAction-dmsPicker'>
        <input
          type='file'
          className={classes.hiddenInput}
          accept={RECEIPT_DMS_TRIGGER_ALLOWED_EXTENSIONS.join(',')}
          onChange={onPick} />
        {buttonIcon}
      </label>
    )
    : (
      <FileInput
        name='receipt_file_input'
        className={classes.fileInput}
        uploader={uploader}
        accept={accept}
        text={{ selectFiles: '', selectFile: '' }} >
        {buttonIcon}
      </FileInput>
    )

  return (
    <div className={classes.root}>
      <Tooltip
        placement='left'
        title={label}>
        <Fab
          size='small'
          className={
            classNames(
              classes.fab,
              {
                [classes.fabClosed]: !isOpen,
              },
            )
          } >
          {picker}
        </Fab>
      </Tooltip>
    </div>
  )
}

export default ReceiptUploadAction
